import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Mail, Lock, Key, Eye, EyeOff, Loader2, ArrowRight, ChevronLeft } from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';

function ClientLoginPage() {
  const navigate = useNavigate();
  const { clientLogin, verify2FA, changePassword, loading } = useClientAuth();
  
  // Flow state: 'login' | 'password_change' | 'totp_enroll' | 'totp_verify'
  const [step, setStep] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [preToken, setPreToken] = useState('');
  
  // Password change state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // 2FA enrollment state
  const [enrollment, setEnrollment] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      const result = await clientLogin(email, password);
      
      if (result.status === 'password_change_required') {
        setPreToken(result.token);
        setStep('password_change');
        toast.info('Please change your temporary password');
      } else if (result.status === 'totp_enrollment_required') {
        setPreToken(result.token);
        setEnrollment(result.enrollment);
        setStep('totp_enroll');
        toast.info('Set up two-factor authentication');
      } else if (result.status === 'totp_required') {
        setPreToken(result.token);
        setStep('totp_verify');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      await changePassword(preToken, password, newPassword);
      toast.success('Password changed! Please login again');
      setStep('login');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    
    try {
      await verify2FA(preToken, totpCode);
      toast.success('Login successful!');
      navigate('/client-portal');
    } catch (error) {
      toast.error(error.message);
      setTotpCode('');
    }
  };

  return (
    <div className="client-login-page">
      <div className="login-bg" />
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <ShieldCheck className="logo-icon" />
            <span className="logo-text"><span className="logo-accent">Threebie</span> Client Portal</span>
          </div>
          <p className="login-subtitle">Compliance Dashboard Access</p>
        </div>

        {/* Step 1: Login */}
        {step === 'login' && (
          <div className="login-card" data-testid="client-login-form">
            <h2>Client Login</h2>
            <p>Enter your email and password</p>
            
            <form onSubmit={handleLogin}>
              <div className="input-group">
                <Mail size={18} className="input-icon" />
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="client-email-input"
                />
              </div>
              
              <div className="input-group">
                <Lock size={18} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="client-password-input"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              
              <button
                type="submit"
                className="btn-primary full-width"
                disabled={loading}
                data-testid="client-login-btn"
              >
                {loading ? <Loader2 className="spin" size={18} /> : 'Sign In'}
                {!loading && <ArrowRight size={18} />}
              </button>
            </form>
            
            <div className="login-divider">
              <Link to="/login" className="staff-link">
                <ChevronLeft size={16} />
                Threebie Staff Login
              </Link>
            </div>
          </div>
        )}

        {/* Step 2: Password Change (First Login) */}
        {step === 'password_change' && (
          <div className="login-card" data-testid="password-change-form">
            <h2>Change Password</h2>
            <p>Your temporary password must be changed</p>
            
            <form onSubmit={handlePasswordChange}>
              <div className="password-requirements">
                <p>Password must contain:</p>
                <ul>
                  <li>At least 12 characters</li>
                  <li>Uppercase and lowercase letters</li>
                  <li>At least one number</li>
                  <li>At least one special character</li>
                </ul>
              </div>
              
              <div className="input-group">
                <Lock size={18} className="input-icon" />
                <input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={12}
                  data-testid="new-password-input"
                />
              </div>
              
              <div className="input-group">
                <Lock size={18} className="input-icon" />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              
              <button
                type="submit"
                className="btn-primary full-width"
                disabled={loading}
                data-testid="change-password-btn"
              >
                {loading ? <Loader2 className="spin" size={18} /> : 'Change Password'}
              </button>
            </form>
          </div>
        )}

        {/* Step 3: TOTP Enrollment */}
        {step === 'totp_enroll' && enrollment && (
          <div className="login-card totp-enroll" data-testid="totp-enroll-form">
            <h2>Set Up Two-Factor Auth</h2>
            <p>Scan this QR code with your authenticator app</p>
            
            <div className="qr-container">
              <img
                src={`data:image/png;base64,${enrollment.qr_code_base64}`}
                alt="2FA QR Code"
                className="qr-code"
              />
            </div>
            
            <div className="manual-entry">
              <p>Or enter this code manually:</p>
              <code className="totp-secret">{enrollment.secret}</code>
            </div>
            
            <div className="backup-codes">
              <p>Save these backup codes in a secure place:</p>
              <div className="codes-grid">
                {enrollment.backup_codes.map((code, i) => (
                  <code key={i}>{code}</code>
                ))}
              </div>
            </div>
            
            <form onSubmit={handle2FAVerify}>
              <div className="input-group">
                <Key size={18} className="input-icon" />
                <input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  pattern="\d{6}"
                  data-testid="totp-code-input"
                />
              </div>
              
              <button
                type="submit"
                className="btn-primary full-width"
                disabled={loading || totpCode.length !== 6}
                data-testid="verify-2fa-btn"
              >
                {loading ? <Loader2 className="spin" size={18} /> : 'Verify & Complete Setup'}
              </button>
            </form>
          </div>
        )}

        {/* Step 4: TOTP Verification (Returning User) */}
        {step === 'totp_verify' && (
          <div className="login-card" data-testid="totp-verify-form">
            <h2>Two-Factor Authentication</h2>
            <p>Enter the code from your authenticator app</p>
            
            <form onSubmit={handle2FAVerify}>
              <div className="input-group totp-input">
                <Key size={18} className="input-icon" />
                <input
                  type="text"
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  pattern="\d{6}"
                  autoFocus
                  data-testid="totp-verify-input"
                />
              </div>
              
              <button
                type="submit"
                className="btn-primary full-width"
                disabled={loading || totpCode.length !== 6}
                data-testid="verify-totp-btn"
              >
                {loading ? <Loader2 className="spin" size={18} /> : 'Verify'}
              </button>
              
              <p className="help-text">
                You can also use a backup code if you've lost access to your authenticator.
              </p>
            </form>
            
            <button
              className="back-btn"
              onClick={() => {
                setStep('login');
                setTotpCode('');
              }}
            >
              <ChevronLeft size={16} />
              Back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientLoginPage;
